import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Crown, Users, DollarSign, TrendingUp, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const defaultForm = { name: '', description: '', price: 0, duration_days: 30, level: 1, features: [], is_recurring: false, is_active: true };

export default function TenantMemberships() {
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showRestrictionDialog, setShowRestrictionDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [restrictionData, setRestrictionData] = useState({ type: '', id: '', planId: '' });

  const fetchData = async () => {
    try {
      const [plansRes, membersRes, statsRes, productsRes, coursesRes] = await Promise.all([
        api.get('/tenant/memberships'),
        api.get('/tenant/members'),
        api.get('/tenant/membership-stats'),
        api.get('/tenant/products'),
        api.get('/tenant/courses')
      ]);
      setPlans(plansRes.data.plans || []);
      setMembers(membersRes.data.members || []);
      setStats(statsRes.data);
      setProducts(productsRes.data.products || []);
      setCourses(coursesRes.data.courses || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/tenant/memberships/${editing}`, form);
      else await api.post('/tenant/memberships', form);
      toast.success('Membership plan disimpan');
      setShowDialog(false);
      setEditing(null);
      setForm(defaultForm);
      fetchData();
    } catch (e) {
      toast.error('Gagal menyimpan');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Hapus membership plan ini?')) {
      await api.delete(`/tenant/memberships/${id}`);
      toast.success('Plan dihapus');
      fetchData();
    }
  };

  const handleSetRestriction = async () => {
    try {
      const endpoint = restrictionData.type === 'product' 
        ? `/tenant/products/${restrictionData.id}/membership`
        : `/tenant/courses/${restrictionData.id}/membership`;
      await api.put(endpoint, { membership_plan_id: restrictionData.planId || null });
      toast.success('Restriction diperbarui');
      setShowRestrictionDialog(false);
      fetchData();
    } catch (e) {
      toast.error('Gagal update restriction');
    }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm(p);
    setShowDialog(true);
  };

  const openRestrictionDialog = (type, item) => {
    setRestrictionData({
      type,
      id: item.id,
      name: type === 'product' ? item.name : item.title,
      planId: item.membership_required || ''
    });
    setShowRestrictionDialog(true);
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
      </div>
    );
  }

  return (
    <div data-testid="tenant-memberships-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Membership</h1>
          <p className="text-sm text-slate-500">Kelola membership plans dan akses konten</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(defaultForm); setShowDialog(true); }} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-membership-btn">
          <Plus size={16} className="mr-2" /> Tambah Plan
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Crown size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.active_plans || 0}</p>
                <p className="text-xs text-slate-500">Active Plans</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.active_members || 0}</p>
                <p className="text-xs text-slate-500">Active Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Rp {fmt(stats.total_revenue)}</p>
                <p className="text-xs text-slate-500">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingUp size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_members || 0}</p>
                <p className="text-xs text-slate-500">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans">Plans ({plans.length})</TabsTrigger>
          <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
          <TabsTrigger value="restrictions">Content Restrictions</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((p) => (
              <Card key={p.id} className="border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                      <Crown size={20} className="text-violet-600" />
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => edit(p)}><Edit size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}><Trash2 size={14} className="text-red-500" /></Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-slate-900">{p.name}</h3>
                    <Badge variant="outline" className="text-xs">Level {p.level}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2">{p.description || 'No description'}</p>
                  <p className="text-2xl font-bold text-[#2D6CDF] mt-3">
                    Rp {fmt(p.price)}
                    <span className="text-sm text-slate-500 font-normal">/{p.duration_days} hari</span>
                  </p>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between text-sm">
                    <span className="text-slate-500">{p.active_members || 0} member aktif</span>
                    <Badge variant="outline" className={p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {p.features?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {p.features.slice(0, 3).map((f, j) => (
                        <span key={j} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{f}</span>
                      ))}
                      {p.features.length > 3 && <span className="text-xs text-slate-400">+{p.features.length - 3}</span>}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {!plans.length && (
              <div className="col-span-full py-12 text-center text-slate-400">
                Belum ada membership plan. Klik "Tambah Plan" untuk membuat.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Member</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Plan</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Expires</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{m.user?.name || '-'}</p>
                        <p className="text-xs text-slate-500">{m.user?.email}</p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Crown size={14} className="text-violet-500" />
                          <span>{m.plan?.name || m.plan_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {m.expires_at ? new Date(m.expires_at).toLocaleDateString('id-ID') : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={
                          m.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                          m.status === 'expired' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>{m.status}</Badge>
                      </td>
                    </tr>
                  ))}
                  {!members.length && (
                    <tr><td colSpan={4} className="py-12 text-center text-slate-400">Belum ada member</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="restrictions" className="mt-6">
          <div className="space-y-6">
            {/* Products */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Produk Digital</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Produk</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Tipe</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Restriction</th>
                      <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.filter(p => p.type === 'digital' || p.type === 'course').map((p) => {
                      const restrictedPlan = plans.find(pl => pl.id === p.membership_required);
                      return (
                        <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="py-3 px-4 font-medium text-slate-900">{p.name}</td>
                          <td className="py-3 px-4"><Badge variant="outline">{p.type}</Badge></td>
                          <td className="py-3 px-4">
                            {restrictedPlan ? (
                              <div className="flex items-center gap-2">
                                <Lock size={14} className="text-amber-500" />
                                <span className="text-amber-700">{restrictedPlan.name}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">No restriction</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button size="sm" variant="outline" onClick={() => openRestrictionDialog('product', p)}>
                              <Lock size={12} className="mr-1" /> Set
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {products.filter(p => p.type === 'digital' || p.type === 'course').length === 0 && (
                      <tr><td colSpan={4} className="py-8 text-center text-slate-400">Tidak ada produk digital</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Courses */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Kursus</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Kursus</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Restriction</th>
                      <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((c) => {
                      const restrictedPlan = plans.find(pl => pl.id === c.membership_required);
                      return (
                        <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="py-3 px-4 font-medium text-slate-900">{c.title}</td>
                          <td className="py-3 px-4">
                            {restrictedPlan ? (
                              <div className="flex items-center gap-2">
                                <Lock size={14} className="text-amber-500" />
                                <span className="text-amber-700">{restrictedPlan.name}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">No restriction</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button size="sm" variant="outline" onClick={() => openRestrictionDialog('course', c)}>
                              <Lock size={12} className="mr-1" /> Set
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {courses.length === 0 && (
                      <tr><td colSpan={3} className="py-8 text-center text-slate-400">Tidak ada kursus</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Plan Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Plan' : 'Tambah Plan'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Plan</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="membership-name-input" />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Harga (Rp)</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Durasi (hari)</Label>
                <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 30 })} />
              </div>
            </div>
            <div>
              <Label>Level (1 = terendah)</Label>
              <Input type="number" value={form.level} onChange={(e) => setForm({ ...form, level: parseInt(e.target.value) || 1 })} />
              <p className="text-xs text-slate-500 mt-1">Member dengan level lebih tinggi bisa akses konten level di bawahnya</p>
            </div>
            <div>
              <Label>Fitur (pisahkan koma)</Label>
              <Input value={form.features?.join(', ')} onChange={(e) => setForm({ ...form, features: e.target.value.split(',').map(f => f.trim()).filter(Boolean) })} />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_recurring} onCheckedChange={(v) => setForm({ ...form, is_recurring: v })} />
                <Label>Recurring</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Batal</Button>
            <Button onClick={handleSave} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-membership-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restriction Dialog */}
      <Dialog open={showRestrictionDialog} onOpenChange={setShowRestrictionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Membership Restriction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">{restrictionData.type === 'product' ? 'Produk' : 'Kursus'}</p>
              <p className="font-medium">{restrictionData.name}</p>
            </div>
            <div>
              <Label>Membership Required</Label>
              <Select value={restrictionData.planId} onValueChange={(v) => setRestrictionData({ ...restrictionData, planId: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih membership (kosongkan jika tidak perlu)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Restriction</SelectItem>
                  {plans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} (Level {p.level})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Hanya member dengan level sama atau lebih tinggi yang bisa akses</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestrictionDialog(false)}>Batal</Button>
            <Button onClick={handleSetRestriction} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
